import {memo, useCallback, useEffect, useState} from 'react';

import styled from 'styled-components';

import {getLocalizationService} from '~/shared/services/localisationService';
import {media} from '~/shared/theme/media';
import {bodyRegular, headingXS, smallRegular} from '~/shared/theme/typography';
import TenbisCard from '~/shared/components/TenbisCard';
import {flipOnLTR, rotateOnRTL} from '~/shared/theme/utils';
import {MoneyCard} from '~/shared/services/bffService/api/interfaces/responses';
import Button from '~/shared/components/Button';
import {MoneycardOtlRuleManagement, User} from '~/shared/store/models';
import {getMoneycardOtlRuleManagementData} from '~/shared/components/Otl/utils';
import {NewTag} from '~/personalArea/components/Sections/MyTenbis/styled';
import {FlexColumn} from '~/shared/theme/FlexLayout';

import useMyTenbisActions from './useMyTenbisActions';
import ActionsSection from './ActionsSection';
import ChevronLeft from './assets/chevronLeft.svg?react';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${media.minLargeMobile`
    gap: 32px;
  `}
  ${media.minLargeTablet`
    gap: 24px;
  `}
`;

const Content = styled.div<{isActive?: boolean}>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  ${({isActive}) => media.minLargeTablet`
    flex-direction: ${isActive ? 'row-reverse' : 'row'};
    justify-content: space-between;
    gap: auto;
  `}
`;

const Header = styled(FlexColumn)`
  gap: 8px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Title = styled.h2`
  ${headingXS}
  color: ${({theme}) => theme.colors.contentDefault};
`;

const Disclaimer = styled.span`
  ${bodyRegular}
  display: block;
`;

const BackButtonContainer = styled.div`
  ${flipOnLTR`
    padding-left: 16px;
    border-left: 1px solid ${({theme}) => theme.colors.borderStrong};
  `}
`;

const BackButton = styled(Button)`
  display: flex;
  gap: 4px;
`;

const BackButtonArrow = styled(ChevronLeft)`
  ${rotateOnRTL}
`;

const BackButtonText = styled.span`
  ${smallRegular}
  display: block;
`;

const StyledTenbisCard = styled(TenbisCard)`
  ${media.minLargeMobile`
    align-self: center;
  `}
  ${media.minLargeTablet`
    align-self: unset;
  `}
`;

type TenbisCardViewProps = {
  currentMoneycard: MoneyCard;
  onBackButtonClick?: (() => void) | null;
  onActivateMoneycardClick?: (id: string) => void;
  userData: User | null;
};

const TenbisCardView = ({
  currentMoneycard,
  onBackButtonClick,
  onActivateMoneycardClick,
  userData,
}: TenbisCardViewProps) => {
  const {t} = getLocalizationService();
  const [lastOtlCardId, setLastOtlCardId] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [otlData, setOtlData] = useState<MoneycardOtlRuleManagement | null>(null);

  const fetchOtlData = useCallback(async () => {
    const cardId = currentMoneycard.id;
    setIsLoading(true);
    try {
      setLastOtlCardId(cardId);
      const otlDataRes = await getMoneycardOtlRuleManagementData(cardId);
      setOtlData(otlDataRes);
    } finally {
      setIsLoading(false);
    }
  }, [currentMoneycard]);

  const actions = useMyTenbisActions({currentMoneycard, otlData, fetchOtl: fetchOtlData});

  const activateCardHandler = () => {
    if (onActivateMoneycardClick) {
      onActivateMoneycardClick(currentMoneycard.id);
    }
  };

  useEffect(() => {
    const cardId = currentMoneycard.id;
    if (!isLoading && !!cardId && lastOtlCardId !== cardId) {
      fetchOtlData();
    }
  }, [currentMoneycard, isLoading, lastOtlCardId, fetchOtlData]);

  const cardNumber = `XXXX-XXXX-XXXX-${currentMoneycard.cardLastDigits}`;

  return (
    <Root>
      <Header>
        <TitleContainer>
          {onBackButtonClick && (
          <BackButtonContainer>
            <BackButton onClick={onBackButtonClick}>
              <BackButtonArrow />
              <BackButtonText>
                {t('back_2')}
              </BackButtonText>
            </BackButton>
          </BackButtonContainer>
        )}
          <Title>{t('tenbis_card_xxx', {fourDigits: currentMoneycard.cardLastDigits})}</Title>
          {!currentMoneycard.isActivated && <NewTag>{t('new_card')}</NewTag>}
        </TitleContainer>
        {!currentMoneycard.isActivated && <Disclaimer>{t('the_card_has_not_been_activated_yet_it_must_be_activated_to_start_using_it')}</Disclaimer>}
      </Header>
      <Content isActive={currentMoneycard.isActivated}>
        <StyledTenbisCard
          isActive={currentMoneycard.isActivated}
          cardHolderName={`${userData?.firstName} ${userData?.lastName}`}
          cardNumber={cardNumber}
          onActivateCardClick={activateCardHandler}
        />
        {currentMoneycard.isActivated && <ActionsSection actions={actions} />}
      </Content>
    </Root>
  );
};

export default memo(TenbisCardView);
